import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { OptimizedImage } from "@/components/ui/optimized-image"
import { imageSrcSet } from "@/data/location-images"
import { cdnUrl } from "@/lib/cdn"
import type { SeoMeta } from "@/types"

/* ------------------------------------------------------------------ */
/* Inline SVG icons                                                    */
/* ------------------------------------------------------------------ */

function IconMail() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
    </svg>
  )
}

function IconClock() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
  )
}

function IconChat() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 0 1-.825-.242m9.345-8.334a2.126 2.126 0 0 0-.476-.095 48.64 48.64 0 0 0-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0 0 11.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" />
    </svg>
  )
}

function IconShield() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="size-6">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
    </svg>
  )
}

/* ------------------------------------------------------------------ */
/* Contact channels                                                    */
/* ------------------------------------------------------------------ */

const CHANNELS = [
  {
    icon: IconMail,
    title: "Email us",
    description: "For general enquiries, partnerships, or press.",
    detail: "team@quiteright.co.uk",
    href: "mailto:team@quiteright.co.uk",
    cta: "Send an email",
  },
  {
    icon: IconChat,
    title: "Support",
    description: "Need help with your account or a technical issue?",
    detail: "team@quiteright.co.uk",
    href: "mailto:team@quiteright.co.uk",
    cta: "Get support",
  },
  {
    icon: IconShield,
    title: "Data protection",
    description: "Questions about your data, privacy, or GDPR rights.",
    detail: "team@quiteright.co.uk",
    href: "mailto:team@quiteright.co.uk",
    cta: "Contact DPO",
  },
  {
    icon: IconClock,
    title: "Response time",
    description: "We aim to reply within one working day, Monday to Friday.",
    detail: "Mon–Fri, 9am–6pm GMT",
    href: null,
    cta: null,
  },
]

/* ------------------------------------------------------------------ */
/* Page component                                                      */
/* ------------------------------------------------------------------ */

export default function Contact({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        {/* Open Graph */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={meta.image} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        {/* Structured Data - ContactPage */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contact QuiteRight",
          description: meta.description,
          url: meta.canonical,
        })}</script>
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">Contact</p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Let&rsquo;s talk<span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            Whether you&rsquo;re an estate agent curious about video marketing, a letting agency
            looking to scale, or a holiday let owner wanting to stand out - we&rsquo;re here to help.
          </p>
        </div>
      </section>

      {/* Clifton Suspension Bridge image */}
      <section className="border-t border-stone bg-white px-6 pt-16 pb-0">
        <div className="mx-auto max-w-5xl">
          <div className="overflow-hidden rounded-xl border border-stone shadow-sm">
            <OptimizedImage
              eager
              src={cdnUrl("/clifton-bridge-800w.webp")}
              srcSet={imageSrcSet("/clifton-bridge-800w.webp")}
              sizes="(max-width: 768px) 100vw, 768px"
              alt="Watercolour illustration of the Clifton Suspension Bridge spanning the Avon Gorge in Bristol"
              className="w-full"
              width={1024}
              height={512}
            />
          </div>
        </div>
      </section>

      {/* Contact channels */}
      <section className="bg-white px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <div className="grid gap-6 md:grid-cols-2">
            {CHANNELS.map((channel) => {
              const Icon = channel.icon
              return (
                <div key={channel.title} className="rounded-lg border border-stone bg-cream p-6">
                  <span className="flex size-10 items-center justify-center rounded-full bg-red/10 text-red">
                    <Icon />
                  </span>
                  <h2 className="mt-4 font-display text-lg font-semibold text-charcoal">{channel.title}</h2>
                  <p className="mt-2 text-sm text-slate">{channel.description}</p>
                  <p className="mt-3 text-sm font-medium text-charcoal">{channel.detail}</p>
                  {channel.href && channel.cta && (
                    <a
                      href={channel.href}
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-red transition-colors hover:text-deep-red"
                    >
                      {channel.cta}
                      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2} className="size-3">
                        <path d="M3 8h10M9 4l4 4-4 4" />
                      </svg>
                    </a>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Common questions */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <div className="flex flex-col items-center gap-12 md:flex-row md:gap-16">
            <div className="flex-1">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">Before you write</p>
              <h2 className="mt-3 font-display text-3xl font-bold text-charcoal md:text-4xl">
                Check the FAQ first<span className="text-red">.</span>
              </h2>
              <p className="mt-4 text-base leading-relaxed text-slate">
                We&rsquo;ve answered the most common questions about pricing, templates, social
                publishing, and team accounts. You might find what you need without waiting for a reply.
              </p>
              <div className="mt-6">
                <Link
                  href="/faq"
                  className="inline-flex items-center gap-2 rounded-md border border-stone bg-white px-5 py-2.5 text-sm font-medium text-charcoal transition-colors hover:bg-stone"
                >
                  Browse the FAQ
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2} className="size-3">
                    <path d="M3 8h10M9 4l4 4-4 4" />
                  </svg>
                </Link>
              </div>
            </div>
            <div className="flex-1">
              <div className="rounded-lg border border-stone bg-cream p-6">
                <h3 className="font-display text-lg font-semibold text-charcoal">Quick links</h3>
                <ul className="mt-4 space-y-3">
                  {[
                    { label: "How does QuiteRight work?", href: "/how-it-works" },
                    { label: "What features are included?", href: "/features" },
                    { label: "See example videos", href: "/examples" },
                    { label: "Request a demo", href: "/demo" },
                    { label: "Read our privacy policy", href: "/privacy" },
                    { label: "Terms and conditions", href: "/terms" },
                  ].map((link) => (
                    <li key={link.href}>
                      <Link
                        href={link.href}
                        className="flex items-center gap-2 text-sm text-slate transition-colors hover:text-red"
                      >
                        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2} className="size-3 text-red">
                          <path d="M3 8h10M9 4l4 4-4 4" />
                        </svg>
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About QuiteRight — SEO content + outgoing links */}
      <section className="border-t border-stone bg-cream px-6 py-16">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-center font-display text-2xl font-bold text-charcoal md:text-3xl">
            About QuiteRight<span className="text-red">.</span>
          </h2>
          <div className="mx-auto mt-6 max-w-3xl space-y-4 text-sm leading-relaxed text-slate">
            <p>
              QuiteRight is a property video marketing platform built in Clifton, Bristol, for UK
              estate agents, letting agents, and holiday let owners. We turn property listings from
              portals like{" "}
              <a href="https://www.rightmove.co.uk" target="_blank" rel="noopener noreferrer" className="font-medium text-charcoal underline hover:text-red">
                Rightmove
              </a>
              ,{" "}
              <a href="https://www.zoopla.co.uk" target="_blank" rel="noopener noreferrer" className="font-medium text-charcoal underline hover:text-red">
                Zoopla
              </a>
              , and{" "}
              <a href="https://www.onthemarket.com" target="_blank" rel="noopener noreferrer" className="font-medium text-charcoal underline hover:text-red">
                OnTheMarket
              </a>
              {" "}into professional video ads for social media, in under 60 seconds.
            </p>
            <p>
              Whether you&rsquo;re a high-street independent or a multi-branch agency, video marketing
              helps you win more instructions, book more viewings, and stand out in a crowded market.
              We handle everything from scripting and voiceover to rendering and social publishing,
              so you can focus on what you do best: selling and letting property.
            </p>
            <p>
              QuiteRight supports UK estate agents across Bristol, Brighton, Bath, Oxford, and beyond.
              We&rsquo;re proud to be part of the UK property technology ecosystem alongside organisations
              like{" "}
              <a href="https://www.propertymark.co.uk" target="_blank" rel="noopener noreferrer" className="font-medium text-charcoal underline hover:text-red" title="Propertymark, professional body for property agents">
                Propertymark
              </a>
              {" "}and{" "}
              <a href="https://www.tpos.co.uk" target="_blank" rel="noopener noreferrer" className="font-medium text-charcoal underline hover:text-red" title="The Property Ombudsman">
                The Property Ombudsman
              </a>
              . For data protection queries, we comply fully with{" "}
              <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="font-medium text-charcoal underline hover:text-red" title="Information Commissioner's Office, UK data protection regulator">
                UK GDPR as enforced by the ICO
              </a>.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-20">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl">
            Ready to get started<span className="text-red">?</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-warm-grey">
            No credit card required. Create your first video in minutes.
          </p>
          <div className="mt-8">
            <Link
              href="/sign-up"
              className="inline-flex items-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-deep-red"
            >
              Get started free
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
